import { useEffect, useRef, type RefObject } from 'react';
import { useRouterState } from '@tanstack/react-router';
import {
  useUpsertUserPreference,
  useUserPreference,
} from '@/api/rpc/hooks/core/useUserPreference';
import {
  isTrackedPath,
  parsePageUsage,
  recordVisit,
  type PageUsage,
} from './pageUsage';

/**
 * Visits are batched: the write happens this long after the last navigation, so
 * rapid navigation costs one write, not one per page.
 */
const FLUSH_DELAY_MS = 10_000;

type Mutate = ReturnType<typeof useUpsertUserPreference>['mutate'];

/** Writes the pending batch, if any. Takes refs, so it needs no memoizing. */
function flushPending(
  pending: RefObject<PageUsage | null>,
  mutate: RefObject<Mutate>,
) {
  if (!pending.current) return;
  mutate.current({ key: 'pageUsage', value: { pages: pending.current } });
  pending.current = null;
}

/**
 * Records which pages the signed-in user opens (count + recency) in their
 * `pageUsage` preference, for the personalized Home page. Mounted once in the
 * authenticated shell. Nothing is recorded until the stored history has loaded,
 * so a slow or failed read can never overwrite it with an empty one.
 */
export function usePageUsageTracker(): void {
  const leafPath = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.fullPath,
  });
  const preference = useUserPreference('pageUsage');
  const { mutate } = useUpsertUserPreference();
  const loaded = preference.isSuccess;
  const stored = preference.data?.value;

  // Refs are only touched in effects and callbacks, never during render.
  const pending = useRef<PageUsage | null>(null);
  const storedRef = useRef(stored);
  const mutateRef = useRef(mutate);
  useEffect(() => {
    storedRef.current = stored;
    mutateRef.current = mutate;
  });

  // `stored` is only the starting point for a batch (read through a ref), so
  // the server echoing our own writes back never re-records a visit.
  useEffect(() => {
    if (!loaded || !isTrackedPath(leafPath)) return;
    const base = pending.current ?? parsePageUsage(storedRef.current);
    pending.current = recordVisit(base, leafPath, new Date());
    const timer = setTimeout(
      () => flushPending(pending, mutateRef),
      FLUSH_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [leafPath, loaded]);

  // Don't lose the last batch when the tab is hidden or the shell unmounts.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        flushPending(pending, mutateRef);
      }
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      flushPending(pending, mutateRef);
    };
  }, []);
}
